// Skin Selector — main menu skin picker with 3D preview thumbnails
import * as THREE from 'three';
import { SKIN_MANIFEST, type SkinDefinition } from '../cosmetics/skin-manifest';
import { buildSkinMesh } from '../cosmetics/skin-builders';
import { settingsStore } from '../core/settings';

const THUMB_SIZE = 128;

// Dispose all GPU resources held by a Three.js Object3D subtree.
// Walks every Mesh, disposing geometry, materials, and any textures attached
// to standard PBR map slots. Required because three.js does not auto-free
// GPU buffers when an object is removed from a scene.
function disposeModel(model: THREE.Object3D): void {
  model.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!(mesh as THREE.Mesh).isMesh) return;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material;
    if (!mat) return;
    const materials = Array.isArray(mat) ? mat : [mat];
    for (const m of materials) {
      disposeMaterial(m);
    }
  });
}

function disposeMaterial(material: THREE.Material): void {
  const m = material as THREE.Material & {
    map?: THREE.Texture | null;
    normalMap?: THREE.Texture | null;
    roughnessMap?: THREE.Texture | null;
    aoMap?: THREE.Texture | null;
  };
  if (m.map) m.map.dispose();
  if (m.normalMap) m.normalMap.dispose();
  if (m.roughnessMap) m.roughnessMap.dispose();
  if (m.aoMap) m.aoMap.dispose();
  material.dispose();
}

export class SkinSelector {
  private el: HTMLElement;
  private gridEl: HTMLElement;
  private previewCanvas: HTMLCanvasElement;
  private previewCtx: CanvasRenderingContext2D;
  private nameEl: HTMLElement;
  private teamEl: HTMLElement;
  private searchInput: HTMLInputElement;
  private skinSearchQuery = '';

  // Shared renderer for thumbnails (avoids WebGL context limit)
  private renderer: THREE.WebGLRenderer;
  private camera: THREE.PerspectiveCamera;

  // Preview state
  private previewScene: THREE.Scene | null = null;
  private previewModel: THREE.Group | null = null;
  private previewAngle = -0.3;
  private animFrameId = 0;

  private _selectedSkinId = 0;
  private cards = new Map<number, HTMLElement>();

  constructor() {
    this.el = document.getElementById('skin-selector')!;
    this.gridEl = document.getElementById('skin-grid')!;
    this.previewCanvas = document.getElementById('skin-preview-canvas') as HTMLCanvasElement;
    this.previewCtx = this.previewCanvas.getContext('2d')!;
    this.nameEl = document.getElementById('skin-preview-name')!;
    this.teamEl = document.getElementById('skin-preview-team')!;
    this.searchInput = this.createSearchInput();

    // Single shared offscreen renderer
    const offscreen = document.createElement('canvas');
    offscreen.width = 256;
    offscreen.height = 256;
    this.renderer = new THREE.WebGLRenderer({ canvas: offscreen, antialias: true, alpha: true });
    this.renderer.setSize(256, 256);
    this.renderer.setPixelRatio(1);
    this.renderer.shadowMap.enabled = true;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    this.camera = new THREE.PerspectiveCamera(35, 1, 0.1, 50);
    this.camera.position.set(0.8, 1.4, 2.8);
    this.camera.lookAt(0, 1.0, 0);

    this.mountSearchInput();
    this._selectedSkinId = settingsStore.get().selectedSkinId;
    try {
      this.buildGrid();
      this.selectSkin(this._selectedSkinId);
    } catch (e) {
      // Ensure renderer and animation frame are cleaned up on exception
      if (this.animFrameId) {
        cancelAnimationFrame(this.animFrameId);
        this.animFrameId = 0;
      }
      this.renderer.dispose();
      throw e;
    }
  }

  get selectedSkinId(): number {
    return this._selectedSkinId;
  }

  private createPreviewScene(skinDef: SkinDefinition): THREE.Scene {
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x12121e);
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const key = new THREE.DirectionalLight(0xffeedd, 1.5);
    key.position.set(2, 4, 3);
    key.castShadow = true;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0x8888ff, 0.4);
    fill.position.set(-2, 2, -1);
    scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffffff, 0.6);
    rim.position.set(0, 3, -3);
    scene.add(rim);
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(4, 4),
      new THREE.MeshStandardMaterial({ color: 0x1a1a2e, roughness: 0.9 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    const model = buildSkinMesh(skinDef.id, skinDef.fallbackColor);
    scene.add(model);
    this.previewModel = model;
    return scene;
  }

  private buildGrid(): void {
    this.gridEl.innerHTML = '';
    this.cards.clear();

    const query = this.skinSearchQuery;
    const skins = Array.from(SKIN_MANIFEST.values())
      .filter((skin) => !query || skin.name.toLowerCase().includes(query))
      .sort((a, b) => a.id - b.id);

    if (skins.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = [
        'grid-column:1 / -1',
        'padding:18px 8px',
        'text-align:center',
        'font-family:"Courier New",monospace',
        'font-size:11px',
        'letter-spacing:1px',
        'color:rgba(200,192,144,0.55)',
      ].join(';');
      empty.textContent = 'No skins found';
      this.gridEl.appendChild(empty);
      return;
    }

    // Generate a static thumbnail for each skin
    const thumbScene = new THREE.Scene();
    thumbScene.background = new THREE.Color(0x12121e);
    thumbScene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const key = new THREE.DirectionalLight(0xffeedd, 1.5);
    key.position.set(2, 4, 3);
    thumbScene.add(key);
    const fill = new THREE.DirectionalLight(0x8888ff, 0.4);
    fill.position.set(-2, 2, -1);
    thumbScene.add(fill);

    // Temporarily resize renderer for thumbnails
    this.renderer.setSize(THUMB_SIZE, THUMB_SIZE);

    for (const skin of skins) {
      const card = document.createElement('div');
      card.className = 'ss-card';
      card.classList.toggle('ss-selected', skin.id === this._selectedSkinId);
      card.dataset.skinId = String(skin.id);

      const canvas = document.createElement('canvas');
      canvas.width = THUMB_SIZE;
      canvas.height = THUMB_SIZE;
      card.appendChild(canvas);

      const label = document.createElement('div');
      label.className = 'ss-label';
      label.textContent = skin.name;
      card.appendChild(label);

      // Render thumbnail
      const model = buildSkinMesh(skin.id, skin.fallbackColor);
      model.rotation.y = -0.3;
      thumbScene.add(model);
      this.renderer.render(thumbScene, this.camera);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(this.renderer.domElement, 0, 0);
      thumbScene.remove(model);
      // Free GPU buffers for the per-skin clone; three.js does not auto-dispose
      // geometries/materials/textures when an object leaves a scene.
      disposeModel(model);

      card.addEventListener('click', () => this.selectSkin(skin.id));
      this.gridEl.appendChild(card);
      this.cards.set(skin.id, card);
    }

    // Restore renderer size for preview
    this.renderer.setSize(256, 256);
  }

  private createSearchInput(): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'search';
    input.placeholder = 'Search skins';
    input.setAttribute('aria-label', 'Search skins');
    input.style.cssText = [
      'width:100%',
      'box-sizing:border-box',
      'padding:9px 10px',
      'background:rgba(0,0,0,0.35)',
      'border:1px solid rgba(200,192,144,0.16)',
      'border-radius:4px',
      'color:#e8e0aa',
      'font-family:"Courier New",monospace',
      'font-size:12px',
      'letter-spacing:1px',
      'outline:none',
    ].join(';');
    input.addEventListener('input', () => {
      this.skinSearchQuery = input.value.trim().toLowerCase();
      this.buildGrid();
    });
    return input;
  }

  private mountSearchInput(): void {
    const parent = this.gridEl.parentElement;
    if (!parent) return;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'display:flex;flex:1;width:100%;min-width:0;flex-direction:column;gap:8px;';
    parent.insertBefore(wrapper, this.gridEl);
    wrapper.appendChild(this.searchInput);
    wrapper.appendChild(this.gridEl);
  }

  private selectSkin(skinId: number): void {
    const skin = SKIN_MANIFEST.get(skinId);
    if (!skin) return;

    // Update highlight
    for (const [id, card] of this.cards) {
      card.classList.toggle('ss-selected', id === skinId);
    }

    this._selectedSkinId = skinId;
    settingsStore.update({ selectedSkinId: skinId });
    this.nameEl.textContent = skin.name;

    const teamText = skin.team === 'both' ? 'T / CT' : skin.team.toUpperCase();
    this.teamEl.textContent = teamText;
    this.teamEl.className = 'ss-team-badge team-' + skin.team;

    // Start animated preview
    if (this.animFrameId) cancelAnimationFrame(this.animFrameId);
    // Dispose previous preview model's GPU resources before replacing — without
    // this, cycling all 28 skins leaks 28 sets of geometry/material/texture
    // buffers on the GPU.
    if (this.previewModel) {
      disposeModel(this.previewModel);
      this.previewModel = null;
    }
    this.previewScene = this.createPreviewScene(skin);
    this.previewAngle = -0.3;
    this.animatePreview();
  }

  private animatePreview = (): void => {
    if (!this.previewScene || !this.previewModel) return;
    this.previewAngle += 0.012;
    this.previewModel.rotation.y = this.previewAngle;
    this.renderer.render(this.previewScene, this.camera);
    this.previewCtx.clearRect(0, 0, 256, 256);
    this.previewCtx.drawImage(this.renderer.domElement, 0, 0);
    this.animFrameId = requestAnimationFrame(this.animatePreview);
  };

  show(): void {
    this.el.style.display = 'flex';
  }

  hide(): void {
    this.el.style.display = 'none';
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = 0;
    }
  }

  dispose(): void {
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = 0;
    }
    this.renderer.dispose();
  }
}
